import { apiErrorMessage } from './apiError'

export { apiErrorMessage }
import { BlogModel } from '@/http/models/blog'
import { MediaModel } from '@/http/models/media/Media'

const BLOG_FOLDER = 'blog'


/**
 * File name for a post body: the title as a slug plus the upload time, `.txt` because the media API takes no
 * `text/html` documents. Names are unique per folder and the previous body is only removed after a save, so each
 * upload needs its own name.
 */
export const bodyFileName = (title: string, now = Date.now()): string => {
  const slug = title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
  return `${slug || 'post'}-${now.toString(36)}.txt`
}

/** Id of the root "blog" media folder, created on first use. */
const blogFolderId = async (): Promise<string> => {
  const list = await MediaModel.getRootFolders({ query: { page: 1, length: 100 } })
  if (!list.success) throw new Error(apiErrorMessage(list, 'Could not read media folders'))
  const found = list.data?.result.find((f) => f.name.toLowerCase() === BLOG_FOLDER)
  if (found) return found._id
  const created = await MediaModel.createFolder({ body: { name: BLOG_FOLDER, parent_id: null } })
  if (!created.success || !created.data) throw new Error(apiErrorMessage(created, 'Could not create the blog folder'))
  return created.data.result._id
}

/** Uploads the editor HTML as a text document in the "blog" folder and returns its media id. */
export const uploadBlogBody = async (html: string, title: string): Promise<string> => {
  const name = bodyFileName(title)
  const form = new FormData()
  form.append('folder_id', await blogFolderId())
  form.append('file', new File([html], name, { type: 'text/plain' }))
  form.append('name', name)
  const res = await MediaModel.uploadDocument({ body: form })
  if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not upload the post body'))
  return res.data.result._id
}

/** Reads a post body (HTML in a text file) through the backend: the CDN it is stored on sends no CORS headers. */
export const fetchBlogBody = async (id: string): Promise<string> => {
  const res = await BlogModel.getContent({ params: { id } })
  if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not load the post body'))
  return res.data.result.content
}
